/**
 * Store Settings — shipping rates, sender info for labels, brand basics, payment + Shiprocket info.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2, Truck, Store, CreditCard, Package } from "lucide-react";

const TABS = [
    { key: "store", label: "Store / brand", icon: Store },
    { key: "shipping", label: "Shipping", icon: Truck },
    { key: "payments", label: "Payments", icon: CreditCard },
    { key: "shiprocket", label: "Shiprocket", icon: Package },
];

export default function AdminSettings() {
    const [tab, setTab] = useState("store");
    return (
        <div className="space-y-6" data-testid="admin-settings">
            <header>
                <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">Operations</p>
                <h1 className="font-display text-3xl sm:text-4xl mt-1">Store settings</h1>
            </header>
            <nav className="inline-flex p-1 rounded-full bg-[#FAF7F1] border border-[#E7DED2] overflow-x-auto max-w-full">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`settings-tab-${t.key}`}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-mono-ui whitespace-nowrap transition-colors ${
                                active ? "bg-[#1B1B1F] text-white" : "text-[#6B645C] hover:bg-white"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" /> {t.label}
                        </button>
                    );
                })}
            </nav>
            {tab === "store" && <StoreTab />}
            {tab === "shipping" && <ShippingTab />}
            {tab === "payments" && <PaymentsTab />}
            {tab === "shiprocket" && <ShiprocketTab />}
        </div>
    );
}

function Field({ label, children, hint }) {
    return (
        <label className="block space-y-1.5">
            <span className="block text-[11px] font-mono-ui tracking-[0.15em] uppercase text-[#857C72]">{label}</span>
            {children}
            {hint && <span className="block text-[11px] text-[#857C72]">{hint}</span>}
        </label>
    );
}

const txtCls =
    "w-full px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B1B1F]/10";

function StoreTab() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/admin/settings/store").then((r) => setForm(r.data));
    }, []);
    if (!form) return <Loader />;

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/settings/store", form);
            setForm(data);
            toast.success("Saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-2xl border border-[#E7DED2] bg-white p-6 space-y-4 max-w-3xl">
            <p className="text-sm text-[#6B645C]">
                These details appear on shipping labels, invoices, emails and the footer.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Brand name">
                    <input className={txtCls} value={form.brand_name || ""} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} />
                </Field>
                <Field label="GSTIN">
                    <input className={txtCls} value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
                </Field>
                <Field label="Support email">
                    <input type="email" className={txtCls} value={form.support_email || ""} onChange={(e) => setForm({ ...form, support_email: e.target.value })} />
                </Field>
                <Field label="Support phone">
                    <input className={txtCls} value={form.support_phone || ""} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} />
                </Field>
                <Field label="Instagram handle">
                    <input className={txtCls} value={form.instagram || ""} onChange={(e) => setForm({ ...form, instagram: e.target.value })} />
                </Field>
                <Field label="Country">
                    <input className={txtCls} value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                </Field>
                <div className="sm:col-span-2">
                    <Field label="Address line 1">
                        <input className={txtCls} value={form.address_line1 || ""} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} />
                    </Field>
                </div>
                <div className="sm:col-span-2">
                    <Field label="Address line 2">
                        <input className={txtCls} value={form.address_line2 || ""} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} />
                    </Field>
                </div>
                <Field label="City">
                    <input className={txtCls} value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </Field>
                <Field label="State">
                    <input className={txtCls} value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                </Field>
                <Field label="PIN code">
                    <input className={txtCls} value={form.pincode || ""} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
                </Field>
            </div>
            <div className="pt-2 border-t border-[#E7DED2]">
                <button
                    onClick={save}
                    disabled={saving}
                    className="dw-btn-primary inline-flex items-center gap-1.5 px-5 h-10 text-xs font-mono-ui"
                    data-testid="store-save"
                >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save store details
                </button>
            </div>
        </div>
    );
}

function ShippingTab() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/admin/settings/shipping").then((r) => setForm(r.data));
    }, []);
    if (!form) return <Loader />;

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                flat_rate: Math.round((form.flat_rate_inr ?? form.flat_rate / 100) * 100),
                free_above: Math.round((form.free_above_inr ?? form.free_above / 100) * 100),
                express_rate: form.express_rate_inr != null ? Math.round(form.express_rate_inr * 100) : form.express_rate,
                cod_enabled: !!form.cod_enabled,
                cod_fee: Math.round((form.cod_fee_inr ?? form.cod_fee / 100) * 100),
                currency: form.currency || "INR",
            };
            const { data } = await api.put("/admin/settings/shipping", payload);
            setForm({
                ...data,
                flat_rate_inr: data.flat_rate / 100,
                free_above_inr: data.free_above / 100,
                express_rate_inr: data.express_rate ? data.express_rate / 100 : 0,
                cod_fee_inr: data.cod_fee / 100,
            });
            toast.success("Shipping rates saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const flat_inr = form.flat_rate_inr ?? form.flat_rate / 100;
    const free_inr = form.free_above_inr ?? form.free_above / 100;
    const express_inr = form.express_rate_inr ?? (form.express_rate ? form.express_rate / 100 : 0);
    const cod_inr = form.cod_fee_inr ?? form.cod_fee / 100;

    return (
        <div className="rounded-2xl border border-[#E7DED2] bg-white p-6 space-y-4 max-w-3xl">
            <p className="text-sm text-[#6B645C]">
                Rates apply automatically to every cart preview and order. Existing orders are unaffected.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Standard rate (INR)" hint="Charged when subtotal is below the free-shipping threshold.">
                    <input
                        type="number"
                        min="0"
                        className={txtCls}
                        value={flat_inr}
                        onChange={(e) => setForm({ ...form, flat_rate_inr: parseFloat(e.target.value || "0") })}
                        data-testid="shipping-flat-rate"
                    />
                </Field>
                <Field label="Free shipping above (INR)" hint="Subtotal at or above this amount = free shipping.">
                    <input
                        type="number"
                        min="0"
                        className={txtCls}
                        value={free_inr}
                        onChange={(e) => setForm({ ...form, free_above_inr: parseFloat(e.target.value || "0") })}
                        data-testid="shipping-free-above"
                    />
                </Field>
                <Field label="Express rate (INR)" hint="Optional upgraded shipping option.">
                    <input
                        type="number"
                        min="0"
                        className={txtCls}
                        value={express_inr}
                        onChange={(e) => setForm({ ...form, express_rate_inr: parseFloat(e.target.value || "0") })}
                    />
                </Field>
                <Field label="COD fee (INR)" hint="Extra fee added to cash-on-delivery orders.">
                    <input
                        type="number"
                        min="0"
                        className={txtCls}
                        value={cod_inr}
                        onChange={(e) => setForm({ ...form, cod_fee_inr: parseFloat(e.target.value || "0") })}
                    />
                </Field>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={!!form.cod_enabled}
                        onChange={(e) => setForm({ ...form, cod_enabled: e.target.checked })}
                    />
                    Cash on delivery enabled
                </label>
            </div>
            <div className="pt-2 border-t border-[#E7DED2]">
                <button
                    onClick={save}
                    disabled={saving}
                    className="dw-btn-primary inline-flex items-center gap-1.5 px-5 h-10 text-xs font-mono-ui"
                    data-testid="shipping-save"
                >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save shipping rates
                </button>
            </div>
        </div>
    );
}

function PaymentsTab() {
    return (
        <div className="rounded-2xl border border-[#E7DED2] bg-white p-6 space-y-3 max-w-3xl">
            <h3 className="font-display text-xl">Razorpay</h3>
            <p className="text-sm text-[#6B645C]">
                Razorpay LIVE keys are configured in <code className="font-mono-ui">backend/.env</code>. Live payment capture
                is active on the checkout page.
            </p>
            <div className="rounded-xl bg-[#FFFCF7] border border-[#E7DED2] p-4 text-xs font-mono-ui space-y-1">
                <div>KEY_ID: <strong>rzp_live_•••••••••••••</strong> (last 4 visible only on Razorpay dashboard)</div>
                <div>Mode: <span className="text-emerald-700">LIVE</span></div>
                <div>Webhook secret: set on VPS env</div>
            </div>
            <p className="text-[11px] text-[#857C72]">
                To rotate keys, edit <code className="font-mono-ui">backend/.env</code> and restart the backend service.
            </p>
        </div>
    );
}

function ShiprocketTab() {
    const [cfg, setCfg] = useState(null);
    useEffect(() => {
        api.get("/admin/shipments/config").then((r) => setCfg(r.data)).catch(() => setCfg({ configured: false }));
    }, []);

    return (
        <div className="rounded-2xl border border-[#E7DED2] bg-white p-6 space-y-4 max-w-3xl">
            <h3 className="font-display text-xl">Shiprocket</h3>
            <p className="text-sm text-[#6B645C]">
                Shiprocket credentials live in <code className="font-mono-ui">backend/.env</code> as
                <code className="font-mono-ui">SHIPROCKET_EMAIL</code> + <code className="font-mono-ui">SHIPROCKET_PASSWORD</code>.
                Use your <em>account password</em> (the one you log into <a className="underline" href="https://app.shiprocket.in" target="_blank" rel="noopener noreferrer">app.shiprocket.in</a> with),
                not the API token.
            </p>
            <div className="rounded-xl bg-[#FFFCF7] border border-[#E7DED2] p-4 text-xs space-y-1">
                {cfg == null ? (
                    <Loader />
                ) : !cfg.configured ? (
                    <div className="text-red-600">Not configured.</div>
                ) : cfg.auth_ok ? (
                    <div className="text-emerald-700">Connected · pickup location: <strong>{cfg.pickup_location}</strong> · auto-create on paid: <strong>{cfg.auto_create_on_paid ? "on" : "off"}</strong></div>
                ) : (
                    <div className="text-amber-700">
                        Configured, but Shiprocket returned <strong>403 Access forbidden</strong>. Possible causes:
                        <ul className="list-disc pl-5 mt-2 space-y-0.5">
                            <li>Password in <code>.env</code> is incorrect (must be account login password)</li>
                            <li>API access not yet enabled on your Shiprocket plan</li>
                            <li>IP is being blocked / temporary lockout — try again after 15 min</li>
                            <li>2FA is enabled on the account (disable for API access)</li>
                        </ul>
                    </div>
                )}
            </div>
            <p className="text-[11px] text-[#857C72]">
                After updating production credentials, restart the API service using your deployment process.
            </p>
        </div>
    );
}

function Loader() {
    return (
        <div className="flex items-center gap-2 text-sm text-[#857C72] py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
    );
}
